// ──────────────────────────────────────────────
// DailyCallService — Daily.co Call Object lifecycle wrapper
//
// Extends BaseService for useSyncExternalStore integration
// and typed event emission. Phase 1: standalone, no UI.
// ──────────────────────────────────────────────

import type {
  DailyCall,
  DailyEvent,
  DailyEventObject,
  DailyEventObjectCameraError,
  DailyEventObjectFatalError,
  DailyEventObjectNetworkConnectionEvent,
  DailyEventObjectParticipant,
  DailyEventObjectParticipantLeft,
  DailyEventObjectParticipants,
  DailyEventObjectTrack,
} from '@daily-co/daily-js';
import Daily from '@daily-co/daily-js';
import SentryService from '@services/SentryService';

import { BaseService } from '../upload/BaseService';

import type { DailyCallConfig, DailyCallEventMap, DailyCallState, DailyConnectionStatus } from './types';
import { initialDailyCallState } from './types';

const SENTRY_CATEGORY = 'dailycall.service';

/** Duck-type check for MediaStreamTrack (globalThis.MediaStreamTrack may not exist in test envs) */
function isMediaStreamTrack(value: unknown): value is MediaStreamTrack {
  return typeof value === 'object' && value !== null && 'id' in value && 'kind' in value;
}

export class DailyCallService extends BaseService<DailyCallState, DailyCallEventMap> {
  private callObject: DailyCall | null = null;
  private disposed = false;
  private customAudioTrack: MediaStreamTrack | null = null;
  private customTrackProvider: (() => MediaStreamTrack | null) | null = null;

  // Bound handlers for clean wire/unwire
  private readonly handleJoiningMeeting = this.onJoiningMeeting.bind(this);
  private readonly handleJoinedMeeting = this.onJoinedMeeting.bind(this);
  private readonly handleLeftMeeting = this.onLeftMeeting.bind(this);
  private readonly handleParticipantJoined = this.onParticipantJoined.bind(this);
  private readonly handleParticipantUpdated = this.onParticipantUpdated.bind(this);
  private readonly handleParticipantLeft = this.onParticipantLeft.bind(this);
  private readonly handleTrackStarted = this.onTrackStarted.bind(this);
  private readonly handleTrackStopped = this.onTrackStopped.bind(this);
  private readonly handleError = this.onError.bind(this);
  private readonly handleCameraError = this.onCameraError.bind(this);
  private readonly handleNetworkConnection = this.onNetworkConnection.bind(this);

  constructor() {
    super(initialDailyCallState());
  }

  // ── Private helpers ──

  private setConnectionStatus(status: DailyConnectionStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.updateState({ status });
    this.emit('statusChanged', { status, prev });
  }

  private updateState(partial: Partial<DailyCallState>): void {
    this.setState({
      ...this.state,
      ...partial,
      // Preserve Map identity unless explicitly provided
      remoteAudioTracks: partial.remoteAudioTracks ?? this.state.remoteAudioTracks,
    });
  }

  private extractLocalAudioTrack(): MediaStreamTrack | null {
    if (!this.callObject) return null;
    const local = this.callObject.participants().local;
    return local?.tracks?.audio?.persistentTrack ?? null;
  }

  private wireEventListeners(): void {
    if (!this.callObject) return;
    const co = this.callObject;

    const on = <T extends DailyEvent>(event: T, handler: (e: DailyEventObject<T>) => void) =>
      co.on(event, handler);

    on('joining-meeting', this.handleJoiningMeeting);
    on('joined-meeting', this.handleJoinedMeeting);
    on('left-meeting', this.handleLeftMeeting);
    on('participant-joined', this.handleParticipantJoined);
    on('participant-updated', this.handleParticipantUpdated);
    on('participant-left', this.handleParticipantLeft);
    on('track-started', this.handleTrackStarted);
    on('track-stopped', this.handleTrackStopped);
    on('error', this.handleError);
    on('camera-error', this.handleCameraError);
    on('network-connection', this.handleNetworkConnection);
  }

  private unwireEventListeners(): void {
    if (!this.callObject) return;
    const co = this.callObject;

    co.off('joining-meeting', this.handleJoiningMeeting);
    co.off('joined-meeting', this.handleJoinedMeeting);
    co.off('left-meeting', this.handleLeftMeeting);
    co.off('participant-joined', this.handleParticipantJoined);
    co.off('participant-updated', this.handleParticipantUpdated);
    co.off('participant-left', this.handleParticipantLeft);
    co.off('track-started', this.handleTrackStarted);
    co.off('track-stopped', this.handleTrackStopped);
    co.off('error', this.handleError);
    co.off('camera-error', this.handleCameraError);
    co.off('network-connection', this.handleNetworkConnection);
  }

  // ── Daily event handlers ──

  private onJoiningMeeting(): void {
    this.setConnectionStatus('joining');
  }

  private onJoinedMeeting(event: DailyEventObjectParticipants): void {
    const participantCount = Object.keys(event.participants).length;
    const localAudioTrack = this.extractLocalAudioTrack();
    const local = event.participants.local;

    this.updateState({
      status: 'connected',
      participantCount,
      localAudioTrack,
      localAudioEnabled: local?.tracks?.audio?.state === 'playable',
      localVideoEnabled: local?.tracks?.video?.state === 'playable',
    });

    this.emit('statusChanged', { status: 'connected', prev: 'joining' });
    SentryService.setTag('telehealth_active', 'true');
    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Joined Daily call',
      level: 'info',
      data: { roomUrl: this.state.roomUrl, participantCount },
    });
  }

  private onLeftMeeting(): void {
    const prev = this.state.status;
    this.updateState({
      status: 'idle',
      roomUrl: null,
      participantCount: 0,
      localAudioEnabled: false,
      localVideoEnabled: false,
      error: null,
      localAudioTrack: null,
      remoteAudioTracks: new Map(),
    });

    this.emit('statusChanged', { status: 'idle', prev });
    SentryService.setTag('telehealth_active', 'false');
    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Left Daily call',
      level: 'info',
    });
  }

  private onParticipantJoined(event: DailyEventObjectParticipant): void {
    this.updateState({ participantCount: this.state.participantCount + 1 });
    this.emit('participantJoined', { sessionId: event.participant.session_id });
  }

  private onParticipantUpdated(event: DailyEventObjectParticipant): void {
    const p = event.participant;

    if (p.local) {
      const localAudioTrack = this.extractLocalAudioTrack();
      this.updateState({
        localAudioEnabled: p.tracks?.audio?.state === 'playable',
        localVideoEnabled: p.tracks?.video?.state === 'playable',
        localAudioTrack,
      });
    } else {
      const remoteTrack = p.tracks?.audio?.persistentTrack ?? null;
      if (remoteTrack) {
        const next = new Map(this.state.remoteAudioTracks);
        next.set(p.session_id, remoteTrack);
        this.updateState({ remoteAudioTracks: next });
      }
    }
  }

  private onParticipantLeft(event: DailyEventObjectParticipantLeft): void {
    const sessionId = event.participant.session_id;
    const next = new Map(this.state.remoteAudioTracks);
    next.delete(sessionId);
    this.updateState({
      participantCount: Math.max(0, this.state.participantCount - 1),
      remoteAudioTracks: next,
    });
    this.emit('participantLeft', { sessionId });
  }

  private onTrackStarted(event: DailyEventObjectTrack): void {
    const participant = event.participant;
    if (!participant) return;

    if (participant.local && event.type === 'audio') {
      this.updateState({ localAudioTrack: event.track });
    } else if (!participant.local && event.type === 'audio') {
      const next = new Map(this.state.remoteAudioTracks);
      next.set(participant.session_id, event.track);
      this.updateState({ remoteAudioTracks: next });
    }

    this.emit('trackStarted', {
      sessionId: participant.session_id,
      type: event.type,
      track: event.track,
    });
  }

  private onTrackStopped(event: DailyEventObjectTrack): void {
    const participant = event.participant;
    if (!participant) return;

    if (participant.local && event.type === 'audio') {
      this.updateState({ localAudioTrack: null });
    } else if (!participant.local && event.type === 'audio') {
      const next = new Map(this.state.remoteAudioTracks);
      next.delete(participant.session_id);
      this.updateState({ remoteAudioTracks: next });
    }

    this.emit('trackStopped', {
      sessionId: participant.session_id,
      type: event.type,
    });
  }

  private onError(event: DailyEventObjectFatalError): void {
    const message = event.errorMsg || 'Unknown Daily error';
    this.setConnectionStatus('error');
    this.updateState({ error: message });
    this.emit('error', { message, cause: event.error });
    SentryService.captureException(new Error(message), {
      tags: { source: 'daily_error_event' },
      extra: { error: event.error as unknown },
    });
  }

  private onCameraError(event: DailyEventObjectCameraError): void {
    const message = event.errorMsg?.errorMsg || 'Camera/mic error';
    this.updateState({ error: message });
    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: `Camera error: ${message}`,
      level: 'warning',
      data: {
        type: event.error?.type,
        audioOk: event.errorMsg?.audioOk,
        videoOk: event.errorMsg?.videoOk,
      },
    });
  }

  private onNetworkConnection(event: DailyEventObjectNetworkConnectionEvent): void {
    if (event.event === 'interrupted') {
      this.setConnectionStatus('reconnecting');
    } else if (event.event === 'connected') {
      if (this.state.status === 'reconnecting') {
        this.setConnectionStatus('connected');
      }
    }
  }

  // ── Public API ──

  initialize(config?: DailyCallConfig): void {
    if (this.disposed) {
      throw new Error('DailyCallService has been disposed');
    }

    if (this.callObject) {
      SentryService.addBreadcrumb({
        category: SENTRY_CATEGORY,
        message: 'initialize() called but call object already exists — no-op',
        level: 'info',
      });
      return;
    }

    // Build factory options from config
    const audioSource = config?.audioSource;
    const videoSource = config?.videoSource;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const factoryOpts: Record<string, any> = {};
    if (isMediaStreamTrack(audioSource)) {
      factoryOpts.audioSource = audioSource;
      this.customAudioTrack = audioSource;
    } else if (typeof audioSource === 'string') {
      factoryOpts.audioDeviceId = audioSource;
    }
    if (isMediaStreamTrack(videoSource)) {
      factoryOpts.videoSource = videoSource;
    } else if (typeof videoSource === 'string') {
      factoryOpts.videoDeviceId = videoSource;
    }

    this.callObject = Daily.createCallObject(factoryOpts);
    this.wireEventListeners();

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Daily call object initialized',
      level: 'info',
      data: {
        hasCustomAudioTrack: !!this.customAudioTrack,
      },
    });
  }

  async join(roomUrl: string, token?: string): Promise<void> {
    if (!this.callObject) {
      throw new Error('Call object not initialized. Call initialize() first.');
    }

    this.updateState({ roomUrl });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const joinOpts: Record<string, any> = { url: roomUrl };
    if (token) {
      joinOpts.token = token;
    }

    await this.callObject.join(joinOpts);
  }

  async startCamera(config?: DailyCallConfig): Promise<void> {
    if (!this.callObject) {
      throw new Error('Call object not initialized. Call initialize() first.');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const opts: Record<string, any> = {};
    if (isMediaStreamTrack(config?.audioSource)) {
      opts.audioSource = config.audioSource;
    } else if (typeof config?.audioSource === 'string') {
      opts.audioDeviceId = config.audioSource;
    }
    if (isMediaStreamTrack(config?.videoSource)) {
      opts.videoSource = config.videoSource;
    } else if (typeof config?.videoSource === 'string') {
      opts.videoDeviceId = config.videoSource;
    }

    await this.callObject.startCamera(opts);
  }

  async leave(): Promise<void> {
    if (!this.callObject) return;
    if (this.state.status === 'idle' || this.state.status === 'leaving') return;

    this.setConnectionStatus('leaving');
    await this.callObject.leave();
    // State reset happens in the left-meeting handler
  }

  async destroy(): Promise<void> {
    if (!this.callObject) return;

    if (this.state.status !== 'idle' && this.state.status !== 'leaving') {
      await this.leave();
    }

    this.unwireEventListeners();
    await this.callObject.destroy();
    this.callObject = null;
    this.customAudioTrack = null;
    this.setState(initialDailyCallState());

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Daily call object destroyed',
      level: 'info',
    });
  }

  setLocalAudio(enabled: boolean): void {
    if (!this.callObject) return;

    this.callObject.setLocalAudio(enabled);

    // Track-swap guard: after unmuting, verify Daily didn't replace our custom track
    const expectedTrack = this.customTrackProvider?.() ?? this.customAudioTrack;
    if (enabled && expectedTrack) {
      const currentTrack = this.extractLocalAudioTrack();
      if (currentTrack && currentTrack.id !== expectedTrack.id) {
        SentryService.addBreadcrumb({
          category: SENTRY_CATEGORY,
          message: 'Track swap detected after unmute — re-applying custom track',
          level: 'warning',
          data: {
            expectedTrackId: expectedTrack.id,
            actualTrackId: currentTrack.id,
          },
        });

        this.emit('trackSwapDetected', {
          previousTrackId: expectedTrack.id,
          newTrackId: currentTrack.id,
        });

        this.callObject.updateInputSettings({
          audio: {
            settings: { customTrack: expectedTrack },
          },
        });
      }
    }
  }

  setLocalVideo(enabled: boolean): void {
    if (!this.callObject) return;
    this.callObject.setLocalVideo(enabled);
  }

  /**
   * Register an external provider for the expected audio track.
   * When set, the unmute guard delegates to this provider instead of
   * the internally stored customAudioTrack. Used by DeviceBridgeService.
   */
  setCustomTrackProvider(provider: (() => MediaStreamTrack | null) | null): void {
    this.customTrackProvider = provider;
  }

  async setAudioDevice(deviceIdOrTrack: string | MediaStreamTrack): Promise<void> {
    if (!this.callObject) return;

    const isTrack = isMediaStreamTrack(deviceIdOrTrack);
    if (isTrack) {
      this.customAudioTrack = deviceIdOrTrack;
      await this.callObject.setInputDevicesAsync({
        audioSource: deviceIdOrTrack,
      });
    } else {
      this.customAudioTrack = null;
      await this.callObject.setInputDevicesAsync({
        audioDeviceId: deviceIdOrTrack,
      });
    }

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Audio device changed',
      level: 'info',
      data: { isCustomTrack: isTrack },
    });
  }

  async setVideoDevice(deviceIdOrTrack: string | MediaStreamTrack): Promise<void> {
    if (!this.callObject) return;

    const isTrack = isMediaStreamTrack(deviceIdOrTrack);
    if (isTrack) {
      await this.callObject.setInputDevicesAsync({
        videoSource: deviceIdOrTrack,
      });
    } else {
      await this.callObject.setInputDevicesAsync({
        videoDeviceId: deviceIdOrTrack,
      });
    }

    SentryService.addBreadcrumb({
      category: SENTRY_CATEGORY,
      message: 'Video device changed',
      level: 'info',
      data: { isCustomTrack: isTrack },
    });
  }

  async getAvailableDevices(): Promise<{ devices: MediaDeviceInfo[] }> {
    if (!this.callObject) {
      throw new Error('Call object not initialized. Call initialize() first.');
    }
    const result = await this.callObject.enumerateDevices();
    return { devices: result.devices as unknown as MediaDeviceInfo[] };
  }

  async getCurrentDevices(): Promise<unknown> {
    if (!this.callObject) {
      throw new Error('Call object not initialized. Call initialize() first.');
    }
    return this.callObject.getInputDevices();
  }

  getLocalAudioTrack(): MediaStreamTrack | null {
    return this.state.localAudioTrack;
  }

  getRemoteAudioTracks(): Map<string, MediaStreamTrack> {
    return this.state.remoteAudioTracks;
  }

  /**
   * Read-only access to the underlying Daily call object.
   * Used exclusively for passing to DailyProvider for rendering hooks.
   * DO NOT use for lifecycle management — use DailyCallService methods instead.
   */
  getCallObject(): DailyCall | null {
    return this.callObject;
  }

  // ── Lifecycle ──

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Synchronous cleanup — destroy() is async but we do best-effort
    if (this.callObject) {
      this.unwireEventListeners();
      this.callObject.destroy().catch(() => {
        // Best-effort cleanup during dispose
      });
      this.callObject = null;
    }

    this.customAudioTrack = null;
    super.dispose();
  }
}
